import React, { useContext } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";

import { AuthProvider, AuthContext } from "./context/AuthContext";
import { ToastProvider } from "./context/ToastContext";
import { TaggedMessagesProvider } from "./context/TaggedMessagesContext";
import { KiraProvider, useKiraContext } from "./context/KiraContext";
import { CallProvider } from "./context/CallContext";
import KiraFloat from "./components/kira/KiraFloat";
import KiraModal from "./components/kira/KiraModal";
import KiraWelcomePage from "./pages/KiraWelcomePage";
import ProtectedRoute from "./routes/ProtectedRoute";
import RoleRoute from "./routes/RoleRoute";
import SuperadminRoute from "./routes/SuperadminRoute";
import { ToastContainer } from "./components/ui/Toast";
import ErrorBoundary from "./components/ui/ErrorBoundary";

// ER cases (task 11B) — the module moved inside this app; the separate
// Kwery_cases_frontend and its door are gone. CasesRoute carries the
// session check and the step-up gate; see components/cases/CasesRoute.jsx.
import CasesRoute, { CasesDeepLinkResume } from "./components/cases/CasesRoute";
import MyCases from "./pages/cases/MyCases";
import CaseWorkspace from "./pages/cases/CaseWorkspace";
// ER case ADMINISTRATION (task 24). A separate guard from CasesRoute:
// these two screens are admin-only, the org case list needs a step-up
// and the case type settings must not, and RoleRoute cannot gate either
// — it bypasses superadmin and ignores the roles CSV the backend treats
// as authoritative. See components/admin/cases/AdminCasesRoute.jsx.
import AdminCasesRoute from "./components/admin/cases/AdminCasesRoute";
import OrgCaseList from "./pages/admin/cases/OrgCaseList";
import CaseTypeSettings from "./pages/admin/cases/CaseTypeSettings";

// Chat (post-login home)
import ChatScreen from "./components/chat/ChatScreen";
import CameraDebug from "./components/chat/CameraDebug";


// Login
import LoginPage from "./pages/LoginPage";
import DemoAccessPage from "./pages/DemoAccessPage";
import AccessDenied from "./pages/auth/AccessDenied";
import AcceptInvite from "./pages/auth/AcceptInvite";
import ForgotPasswordPage from "./pages/auth/ForgotPasswordPage";
import ResetPasswordPage from "./pages/auth/ResetPasswordPage";

// Superadmin
import SuperadminLayout from "./layouts/SuperadminLayout";
import SuperadminDashboard from "./pages/superadmin/SuperadminDashboard";
import SuperadminOrgs from "./pages/superadmin/SuperadminOrgs";
import SuperadminUsers from "./pages/superadmin/SuperadminUsers";
import SuperadminStaffInvites from "./pages/superadmin/SuperadminStaffInvites";
import SuperadminModules from "./pages/superadmin/SuperadminModules";
import SuperadminBilling from "./pages/superadmin/SuperadminBilling";
import SuperadminAuditLog from "./pages/superadmin/SuperadminAuditLog";
import SuperadminSettings from "./pages/superadmin/SuperadminSettings";
import SuperadminDemosPage from "./pages/superadmin/SuperadminDemosPage";

// Executive
import ExecutiveDashboardPage from "./pages/executive/ExecutiveDashboardPage";

// Notifications
import NotificationsPage from "./pages/notifications/NotificationsPage";

// Messenger pages
import DashboardMessengerPage from "./pages/messenger/DashboardMessengerPage";
import ProfileMessengerPage from "./pages/messenger/ProfileMessengerPage";
import MyDiaryPage from "./pages/messenger/MyDiaryPage";
import SettingsMessengerPage from "./pages/messenger/SettingsMessengerPage";
import AttendanceMessengerPage from "./pages/messenger/AttendanceMessengerPage";
import LeaveMessengerPage from "./pages/messenger/LeaveMessengerPage";
import PayrollMessengerPage from "./pages/messenger/PayrollMessengerPage";
import PerformanceMessengerPage from "./pages/messenger/PerformanceMessengerPage";
import TicketsMessengerPage from "./pages/messenger/TicketsMessengerPage";
import AnnouncementsPage from "./pages/messenger/AnnouncementsPage";

// Rota
import RotaSchedulerPage from "./pages/rota/RotaSchedulerPage";
import MySchedulePage from "./pages/rota/MySchedulePage";
import SwapRequestsPage from "./pages/rota/SwapRequestsPage";

// Learning & Development
import LearningPage from "./pages/learning/LearningPage";
import CoursePlayerPage from "./pages/learning/CoursePlayerPage";
import ManagerLearningPage from "./pages/learning/ManagerLearningPage";
import AdminLearningPage from "./pages/learning/AdminLearningPage";
import ExecLearningPage from "./pages/learning/ExecLearningPage";

// Manager
import ManagerDashboard from "./components/managerdashboard/ManagerDashboard";
import ManagerProfile from "./components/manager/ManagerProfile";
import EmployeeWorkProfile from "./components/manager/EmployeeWorkProfile";
import TeamAttendancePage from "./components/manager/TeamAttendancePage";
import TeamLeavePage from "./components/manager/TeamLeavePage";
import TeamPayrollPage from "./components/manager/TeamPayrollPage";
import TeamPerformancePage from "./components/manager/TeamPerformancePage";
import TeamTicketsPage from "./components/manager/TeamTicketsPage";

// Admin
import AdminDashboardPage from "./components/admin/AdminDashboardPage";
import AdminProfilePage from "./components/admin/profile/AdminProfilePage";
import AdminAttendancePage from "./components/admin/attendance/AdminAttendancePage";
import AdminLeavePage from "./components/admin/leave/AdminLeavePage";
import AdminPayrollPage from "./components/admin/payroll/AdminPayrollPage";
import AdminPerformancePage from "./components/admin/performance/AdminPerformancePage";
import AdminTicketsPage from "./components/admin/tickets/AdminTicketsPage";
import AdminDocumentsPage from "./components/admin/documents/AdminDocumentsPage";

// Projects
import ProjectsMessengerPage  from "./pages/messenger/ProjectsMessengerPage";
import TeamProjectsPage        from "./components/manager/TeamProjectsPage";
import ProjectManagementPage   from "./components/admin/projects/ProjectManagementPage";

// Sprint 2 — lifecycle + expenses + SSO admin panel
import OnboardingPage   from "./pages/onboarding/OnboardingPage";
import OnboardingScreen from "./pages/onboarding/OnboardingScreen";
import TutorialRedirect from "./pages/help/TutorialRedirect";
import ExpensesPage     from "./pages/expenses/ExpensesPage";

// Sprint 3 — grievance + benefits
// HeadcountPage import removed: the front-end requisition entry-point
// was retired (no nav, no route). The page file lives on in
// pages/workforce/HeadcountPage.jsx and the backend API stays mounted.
import GrievancePage    from "./pages/grievance/GrievancePage";
import BenefitsPage     from "./pages/benefits/BenefitsPage";
import RestructurePage  from "./pages/workforce/RestructurePage";
import AdminModulesPage from "./pages/admin/AdminModulesPage";
import WorkflowEngineLibrary from "./pages/admin/WorkflowEngineLibrary";
import WorkflowEngineBuilder from "./pages/admin/WorkflowEngineBuilder";
import SSOSettingsPanel from "./components/admin/integrations/SSOSettingsPanel";
import MessengerLayout  from "./layout/MessengerLayout";
// DiaryLoginCheckin is retired: the KIRA welcome page's own conversation is
// the morning diary entry now (saved on dismiss), so there is one morning
// write path instead of two racing over the same row.
import DiaryLogoutCheckin from "./components/diary/DiaryLogoutCheckin";
import DiaryNudgeConsent from "./components/diary/DiaryNudgeConsent";

// KiraGate — float bubble + modal.  Hidden on auth pages (login, welcome,
// invite acceptance) and during auth resolution.  Welcome itself lives on
// its own route (/welcome), not inside KiraGate.
function KiraGate() {
  const location = useLocation();
  const auth     = useContext(AuthContext);
  const kira     = useKiraContext();

  // Never show K.I.R.A on auth-flow pages — those screens own the chrome.
  const HIDDEN_PATHS = ['/login', '/welcome', '/accept-invite', '/forgot-password', '/reset-password', '/demo/'];
  if (HIDDEN_PATHS.some((p) => location.pathname.startsWith(p))) return null;

  if (
    auth?.loading ||
    !auth?.isAuthenticated ||
    !auth?.user ||
    auth.user.role === 'superadmin'
  ) return null;

  return (
    <>
      <KiraFloat
        emotion={kira.emotion}
        hasInsight={kira.hasInsight}
        unreadCount={kira.unreadCount}
        isTyping={kira.isTyping}
        onClick={kira.openKira}
        isModalOpen={kira.isOpen}
      />
      {kira.isOpen && (
        <KiraModal
          isOpen={kira.isOpen}
          onClose={kira.closeKira}
          messages={kira.messages}
          emotion={kira.emotion}
          isLoading={kira.isLoading}
          isTyping={kira.isTyping}
          onSendMessage={kira.sendMessage}
          onTyping={kira.noticeTyping}
          pendingAction={kira.pendingAction}
          onConfirmAction={kira.confirmAction}
          onCancelAction={kira.cancelAction}
          position={kira.modalPosition}
          onPositionChange={kira.setModalPosition}
          actionCard={kira.actionCard}
          actionList={kira.actionList}
          isExecuting={kira.isExecuting}
          onSelectActionItem={kira.selectActionItem}
          onConfirmActionCard={kira.confirmActionCard}
          onCancelActionCard={kira.cancelActionCard}
          createThread={kira.createThread}
          setMessages={kira.setMessages}
          loadThreadMessages={kira.loadThreadMessages}
          setThreadId={kira.setThreadId}
        />
      )}
    </>
  );
}

// AppContent — pass-through.  Welcome is now its own route; gating happens
// in LoginPage's redirect choice and KiraGate's path-check.
function AppContent({ children }) {
  return children;
}

export default function App() {
  return (
    <AuthProvider>
      <ToastProvider>
      <TaggedMessagesProvider>
      <BrowserRouter>
      <KiraProvider>
      <AppContent>
        <CallProvider>
        <ErrorBoundary>
        <Routes>


          {/* ---------------- ROOT ---------------- */}
<Route
  path="/"
  element={
    <ProtectedRoute>
      <Navigate to="/login" replace />
    </ProtectedRoute>
  }
/>


          {/* ---------------- LOGIN ---------------- */}
          <Route path="/login" element={<LoginPage />} />

          {/* ---------------- HELP / TUTORIAL ---------------- */}
          {/* Role-aware bridge to the standalone HTML walkthroughs in /public */}
          <Route path="/help/tutorial" element={<TutorialRedirect />} />

          {/* ---------------- PUBLIC DEMO ACCESS ---------------- */}
          <Route path="/demo/:code" element={<DemoAccessPage />} />

          {/* ---------------- WELCOME (POST-LOGIN, PROTECTED) ---------------- */}
          <Route
            path="/welcome"
            element={
              <ProtectedRoute>
                <KiraWelcomePage />
              </ProtectedRoute>
            }
          />

          {/* ---------------- ACCEPT INVITE (PUBLIC) ---------------- */}
          <Route path="/accept-invite" element={<AcceptInvite />} />

          {/* ---------------- PASSWORD RESET (PUBLIC) ---------------- */}
          <Route path="/forgot-password" element={<ForgotPasswordPage />} />
          <Route path="/reset-password" element={<ResetPasswordPage />} />

          {/* ---------------- SUPERADMIN ---------------- */}
          <Route
            path="/superadmin"
            element={
              <SuperadminRoute>
                <SuperadminLayout>
                  <SuperadminDashboard />
                </SuperadminLayout>
              </SuperadminRoute>
            }
          />
          <Route
            path="/superadmin/orgs"
            element={
              <SuperadminRoute>
                <SuperadminLayout>
                  <SuperadminOrgs />
                </SuperadminLayout>
              </SuperadminRoute>
            }
          />
          <Route
            path="/superadmin/users"
            element={
              <SuperadminRoute>
                <SuperadminLayout>
                  <SuperadminUsers />
                </SuperadminLayout>
              </SuperadminRoute>
            }
          />
          <Route
            path="/superadmin/staff-invites"
            element={
              <SuperadminRoute>
                <SuperadminLayout>
                  <SuperadminStaffInvites />
                </SuperadminLayout>
              </SuperadminRoute>
            }
          />
          <Route
            path="/superadmin/modules"
            element={
              <SuperadminRoute>
                <SuperadminLayout>
                  <SuperadminModules />
                </SuperadminLayout>
              </SuperadminRoute>
            }
          />
          <Route
            path="/superadmin/billing"
            element={
              <SuperadminRoute>
                <SuperadminLayout>
                  <SuperadminBilling />
                </SuperadminLayout>
              </SuperadminRoute>
            }
          />
          <Route
            path="/superadmin/demos"
            element={
              <SuperadminRoute>
                <SuperadminLayout>
                  <SuperadminDemosPage />
                </SuperadminLayout>
              </SuperadminRoute>
            }
          />
          <Route
            path="/superadmin/audit"
            element={
              <SuperadminRoute>
                <SuperadminLayout>
                  <SuperadminAuditLog />
                </SuperadminLayout>
              </SuperadminRoute>
            }
          />
          <Route
            path="/superadmin/settings"
            element={
              <SuperadminRoute>
                <SuperadminLayout>
                  <SuperadminSettings />
                </SuperadminLayout>
              </SuperadminRoute>
            }
          />

          {/* ---------------- CHAT (ALWAYS AFTER LOGIN) ---------------- */}
          <Route
            path="/chat"
            element={
              <ProtectedRoute>
                <ChatScreen />
                <CameraDebug />
              </ProtectedRoute>
            }
          />

          <Route path="/403" element={<AccessDenied />} />

          {/* ---------------- MESSENGER ---------------- */}
          <Route
            path="/messenger/dashboard"
            element={
              <ProtectedRoute>
                <DashboardMessengerPage />
              </ProtectedRoute>
            }
          />

          <Route
            path="/messenger/profile"
            element={
              <ProtectedRoute>
                <ProfileMessengerPage />
              </ProtectedRoute>
            }
          />

          <Route
            path="/messenger/settings"
            element={
              <ProtectedRoute>
                <SettingsMessengerPage />
              </ProtectedRoute>
            }
          />

          <Route
            path="/messenger/diary"
            element={
              <ProtectedRoute>
                <MyDiaryPage />
              </ProtectedRoute>
            }
          />

          <Route
            path="/messenger/attendance"
            element={
              <ProtectedRoute>
                <AttendanceMessengerPage />
              </ProtectedRoute>
            }
          />

          <Route
            path="/messenger/leave"
            element={
              <ProtectedRoute>
                <LeaveMessengerPage />
              </ProtectedRoute>
            }
          />

          <Route
            path="/messenger/payroll"
            element={
              <ProtectedRoute>
                <PayrollMessengerPage />
              </ProtectedRoute>
            }
          />

          <Route
            path="/messenger/performance"
            element={
              <ProtectedRoute>
                <PerformanceMessengerPage />
              </ProtectedRoute>
            }
          />

          <Route
            path="/messenger/tickets"
            element={
              <ProtectedRoute>
                <TicketsMessengerPage />
              </ProtectedRoute>
            }
          />

          <Route
            path="/messenger/projects"
            element={
              <ProtectedRoute>
                <ProjectsMessengerPage />
              </ProtectedRoute>
            }
          />

          <Route
            path="/messenger/announcements"
            element={
              <ProtectedRoute>
                <AnnouncementsPage />
              </ProtectedRoute>
            }
          />

          <Route
            path="/messenger/notifications"
            element={
              <ProtectedRoute>
                <NotificationsPage />
              </ProtectedRoute>
            }
          />

          {/* Sprint 2 — onboarding, expenses, admin integrations */}
          <Route
            path="/messenger/onboarding"
            element={
              <ProtectedRoute>
                <OnboardingPage />
              </ProtectedRoute>
            }
          />
          {/* Post-login welcome modal — routed BEFORE the main app. */}
          <Route
            path="/onboarding"
            element={
              <ProtectedRoute>
                <OnboardingScreen />
              </ProtectedRoute>
            }
          />
          {/* Phase 5 — /messenger/expenses now redirects to the Payroll tab. */}
          <Route
            path="/messenger/expenses"
            element={<Navigate to="/messenger/payroll?tab=expenses" replace />}
          />
          <Route
            path="/admin/expenses"
            element={
              <ProtectedRoute>
                <ExpensesPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/settings/integrations"
            element={
              <ProtectedRoute>
                <MessengerLayout>
                  <div className="max-w-4xl mx-auto p-6">
                    <header className="mb-5">
                      <h1 className="text-xl font-semibold text-white">Integrations</h1>
                      <p className="text-xs text-white/40 mt-1">Configure SSO + directory sync providers.</p>
                    </header>
                    <SSOSettingsPanel />
                  </div>
                </MessengerLayout>
              </ProtectedRoute>
            }
          />

          {/* Sprint 3 — grievance, benefits */}
          {/* /messenger/headcount route retired — HeadcountPage.jsx file
              kept in pages/workforce/ and backend endpoints remain mounted
              under /api/workforce/requisitions for future re-introduction. */}
          <Route
            path="/messenger/grievance"
            element={
              <ProtectedRoute>
                <GrievancePage />
              </ProtectedRoute>
            }
          />
          {/* Phase 5 — /messenger/benefits now redirects to the Payroll tab. */}
          <Route
            path="/messenger/benefits"
            element={<Navigate to="/messenger/payroll?tab=benefits" replace />}
          />
          <Route
            path="/admin/restructure"
            element={
              <RoleRoute allowed={["admin", "executive", "superadmin"]}>
                <RestructurePage />
              </RoleRoute>
            }
          />
          <Route
            path="/admin/modules"
            element={
              <RoleRoute allowed={["admin", "executive", "superadmin"]}>
                <AdminModulesPage />
              </RoleRoute>
            }
          />

          {/* Workflow Builder — the library (Phase 4).

              THE CANONICAL WAY IN IS Admin Settings -> Workflows
              (/admin/documents?tab=workflows), which mounts the same
              component. This route stays so existing deep links resolve.

              Not to be confused with the legacy approval-chain feature,
              which lives on the "Approval chains" tab beside it. */}
          <Route
            path="/admin/workflow-engine"
            element={
              <RoleRoute allowed={["admin", "executive", "superadmin"]}>
                <WorkflowEngineLibrary />
              </RoleRoute>
            }
          />
          {/* The builder (Phase 5), keyed on the WORKFLOW KEY rather than
              its uuid: the key is the stable name across a fork, so a
              bookmarked builder URL keeps working after this organisation
              takes its own copy instead of pointing at the shared row it
              has stopped using. */}
          <Route
            path="/admin/workflow-engine/:key"
            element={
              <RoleRoute allowed={["admin", "executive", "superadmin"]}>
                <WorkflowEngineBuilder />
              </RoleRoute>
            }
          />

          {/* ---------------- ROTA ---------------- */}
          <Route
            path="/rota"
            element={
              <RoleRoute allowed={["manager", "admin", "executive", "superadmin"]}>
                <RotaSchedulerPage />
              </RoleRoute>
            }
          />
          <Route
            path="/my-schedule"
            element={
              <ProtectedRoute>
                <MySchedulePage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/my-schedule/swaps"
            element={
              <ProtectedRoute>
                <SwapRequestsPage />
              </ProtectedRoute>
            }
          />

          {/* ---------------- LEARNING & DEVELOPMENT ---------------- */}
          <Route
            path="/messenger/learning"
            element={
              <ProtectedRoute>
                <LearningPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/messenger/learning/course/:id"
            element={
              <ProtectedRoute>
                <CoursePlayerPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/messenger/learning/team"
            element={
              <RoleRoute allowed={["manager", "admin", "executive", "superadmin"]}>
                <ManagerLearningPage />
              </RoleRoute>
            }
          />
          <Route
            path="/admin/learning"
            element={
              <RoleRoute allowed={["admin", "superadmin"]}>
                <AdminLearningPage />
              </RoleRoute>
            }
          />
          <Route
            path="/executive/learning"
            element={
              <RoleRoute allowed={["executive", "admin", "superadmin"]}>
                <ExecLearningPage />
              </RoleRoute>
            }
          />

          {/* ---------------- MANAGER ---------------- */}
          <Route
            path="/manager-dashboard"
            element={
              <RoleRoute allowed={["manager", "admin", "executive", "superadmin"]}>
                <ManagerDashboard />
              </RoleRoute>
            }
          />

          <Route
            path="/manager-profile"
            element={
              <RoleRoute allowed={["manager", "admin", "executive", "superadmin"]}>
                <ManagerProfile />
              </RoleRoute>
            }
          />

          <Route
            path="/manager/employee/:id"
            element={
              <RoleRoute allowed={["manager", "admin", "executive", "superadmin"]}>
                <EmployeeWorkProfile />
              </RoleRoute>
            }
          />

          <Route
            path="/manager/team-attendance"
            element={
              <RoleRoute allowed={["manager", "admin", "executive", "superadmin"]}>
                <TeamAttendancePage />
              </RoleRoute>
            }
          />

          <Route
            path="/manager/team-leave"
            element={
              <RoleRoute allowed={["manager", "admin", "executive", "superadmin"]}>
                <TeamLeavePage />
              </RoleRoute>
            }
          />

          <Route
            path="/manager/team-payroll"
            element={
              <RoleRoute allowed={["manager", "admin", "executive", "superadmin"]}>
                <TeamPayrollPage />
              </RoleRoute>
            }
          />

          <Route
            path="/manager/team-performance"
            element={
              <RoleRoute allowed={["manager", "admin", "executive", "superadmin"]}>
                <TeamPerformancePage />
              </RoleRoute>
            }
          />

          <Route
            path="/manager/team-tickets"
            element={
              <RoleRoute allowed={["manager", "admin", "executive", "superadmin"]}>
                <TeamTicketsPage />
              </RoleRoute>
            }
          />

          <Route
            path="/manager/team-projects"
            element={
              <RoleRoute allowed={["manager", "admin", "executive", "superadmin"]}>
                <TeamProjectsPage />
              </RoleRoute>
            }
          />

          {/* ---------------- ADMIN ---------------- */}
          <Route
            path="/admin/dashboard"
            element={
              <RoleRoute allowed={["admin", "superadmin"]}>
                <AdminDashboardPage />
              </RoleRoute>
            }
          />

          <Route
            path="/admin/profile"
            element={
              <RoleRoute allowed={["admin", "superadmin"]}>
                <AdminProfilePage />
              </RoleRoute>
            }
          />

          <Route
            path="/admin/profile/:userId"
            element={
              <RoleRoute allowed={["admin", "superadmin"]}>
                <ProfileMessengerPage />
              </RoleRoute>
            }
          />

          
          <Route
  path="/profile/:userId"
  element={
    <ProtectedRoute>
      <ProfileMessengerPage />
    </ProtectedRoute>
  }
/>


<Route
  path="/profile"
  element={
    <ProtectedRoute>
      <ProfileMessengerPage />
    </ProtectedRoute>
  }
/>


          <Route
            path="/admin/attendance"
            element={
              <RoleRoute allowed={["admin", "superadmin"]}>
                <AdminAttendancePage />
              </RoleRoute>
            }
          />

          <Route
            path="/admin/leave"
            element={
              <RoleRoute allowed={["admin", "superadmin"]}>
                <AdminLeavePage />
              </RoleRoute>
            }
          />

          <Route
            path="/admin/payroll"
            element={
              <RoleRoute allowed={["admin", "superadmin"]}>
                <AdminPayrollPage />
              </RoleRoute>
            }
          />

          <Route
            path="/admin/performance"
            element={
              <RoleRoute allowed={["admin", "superadmin"]}>
                <AdminPerformancePage />
              </RoleRoute>
            }
          />

          <Route
            path="/admin/tickets"
            element={
              <RoleRoute allowed={["admin", "superadmin"]}>
                <AdminTicketsPage />
              </RoleRoute>
            }
          />

          <Route
            path="/admin/documents"
            element={
              <RoleRoute allowed={["admin", "superadmin"]}>
                <AdminDocumentsPage />
              </RoleRoute>
            }
          />

          <Route
            path="/admin/projects"
            element={
              <RoleRoute allowed={["admin", "superadmin"]}>
                <ProjectManagementPage />
              </RoleRoute>
            }
          />

          <Route 
            path="/executive/dashboard"
            element={
              <RoleRoute allowed={["executive","admin","superadmin"]}>
                <ExecutiveDashboardPage />
              </RoleRoute>
            }
          />


          {/* ---------------- REDIRECTS ---------------- */}
          <Route
            path="/messenger"
            element={<Navigate to="/messenger/profile" replace />}
          />

          <Route
            path="/managerdashboard"
            element={<Navigate to="/manager-dashboard" replace />}
          />

          {/* ---------------- 404 ---------------- */}
          {/* ---------------- ER CASES ---------------- */}
          {/*
            TWO ROUTES AND NO NESTED LAYOUT. App.js is a flat list of
            sibling <Route> elements and react-router 7 would happily
            take a parent route with an <Outlet />, but introducing the
            first nested layout in the file for two paths would leave
            the app with two routing conventions and no rule for which
            to use next. The shared chrome is a component the two pages
            both render (components/cases/Shell.jsx) rather than a
            layout route, which is how the rest of this file already
            does it.

            NOT ProtectedRoute. CasesRoute does the same session check
            and additionally remembers where the user was going, which
            a subject or a witness arriving by link depends on — they
            correctly have no Cases icon to find their way back with.
          */}
          <Route
            path="/cases"
            element={
              <CasesRoute>
                <MyCases />
              </CasesRoute>
            }
          />
          <Route
            path="/cases/:caseId"
            element={
              <CasesRoute>
                <CaseWorkspace />
              </CasesRoute>
            }
          />

          {/* ---------------- ER CASE ADMINISTRATION (task 24) ----------------
              TWO SCREENS, ONE GUARD, TWO DIFFERENT ANSWERS ABOUT STEP-UP.

              /admin/cases        the organisation's caseload. Case data —
                                  thin, but real, and it carries a person's
                                  name — so it is behind the same step-up
                                  every other reader of case data passes.

              /admin/cases/types  the tenant's own procedures. NOT behind
                                  step-up: a stages array names no employee,
                                  and an MFA prompt in front of routine
                                  configuration is how a step-up prompt stops
                                  meaning anything where it matters. The
                                  backend enforces the same split —
                                  er_cases/admin_routes.py attaches
                                  require_stepup to the case list route alone.

              DECLARED IN THIS ORDER, literal before parameterised, matching
              the convention the ER router itself enforces at import. Neither
              path collides with "/cases/:caseId" — different first segment —
              but the ordering rule is followed rather than reasoned about.

              NOT RoleRoute. It bypasses superadmin, which must never reach a
              customer's disciplinary caseload, and it reads user.role while
              ignoring the `roles` CSV the backend unions in. AdminCasesRoute
              mirrors the backend's own predicate instead. */}
          <Route
            path="/admin/cases"
            element={
              <AdminCasesRoute stepUp>
                <OrgCaseList />
              </AdminCasesRoute>
            }
          />
          <Route
            path="/admin/cases/types"
            element={
              <AdminCasesRoute>
                <CaseTypeSettings />
              </AdminCasesRoute>
            }
          />

          <Route
            path="*"
            element={
              <div style={{ color: "#00FF99", padding: 16 }}>
                404: Route not found
              </div>
            }
          />
        </Routes>
        </ErrorBoundary>
        <CasesDeepLinkResume />
        <KiraGate />
        <DiaryLogoutCheckin />
        <DiaryNudgeConsent />
        <ToastContainer />
        </CallProvider>
      </AppContent>
      </KiraProvider>
      </BrowserRouter>
      </TaggedMessagesProvider>
      </ToastProvider>
    </AuthProvider>
  );
}